import {ApplicationConfig,ErrorHandler,isDevMode,inject,LOCALE_ID,Provider,provideBrowserGlobalErrorListeners,provideZonelessChangeDetection} from '@angular/core';
import {provideRouter,withInMemoryScrolling,withPreloading,withRouterConfig} from '@angular/router';
import {routes} from './app.routes';
import {provideClientHydration,withI18nSupport} from '@angular/platform-browser';
import {provideServiceWorker} from '@angular/service-worker';
import {provideAnimationsAsync} from '@angular/platform-browser/animations/async';
import {provideHttpClient,withInterceptors} from '@angular/common/http';
import {provideApollo} from 'apollo-angular';
import {HttpLink} from 'apollo-angular/http';
import {ApolloLink,InMemoryCache} from '@apollo/client';
import {provideFirebase} from './common/firebase/firebase.providers';
import {provideConsentTracking} from './common/consent/consent.providers';
import {MAT_DATE_FORMATS,MAT_DATE_LOCALE} from '@angular/material/core';
import {MAT_DIALOG_DEFAULT_OPTIONS} from '@angular/material/dialog';
import {MAT_SNACK_BAR_DEFAULT_OPTIONS} from '@angular/material/snack-bar';
import {environment} from '../environments/environment';
import {DATE_PIPE_DEFAULT_OPTIONS} from '@angular/common';
import {SelectivePreloadingStrategyService} from './common/selective-preloading-strategy.service';
import {provideNgxWebstorage,withLocalStorage,withNgxWebstorageConfig,withSessionStorage} from 'ngx-webstorage';
import {CookieService} from 'ngx-cookie-service';
import {AuthenticationService} from './authentication/authentication.service';
import {AppService} from './app.service';
import {authenticationInterceptor} from './authentication/authentication.interceptor';
import {httpErrorInterceptor} from './common/http-error.interceptor';
import {GlobalErrorHandler} from './common/global-error-handler.service';
import {provideGalleryOptions} from 'ng-gallery';
import {IconsProvider} from './common/icons/icons.provider';

const appDefaultOptions:Provider[]=[
	{
		provide:DATE_PIPE_DEFAULT_OPTIONS,
		useValue:{
			dateFormat:'d MMM y',
			timezone:'-0500'
		}
	},
	{provide:MAT_DATE_LOCALE,useFactory:()=>inject(LOCALE_ID)==='es' ? 'es-CO' : 'en-US'},
	{
		provide:MAT_DATE_FORMATS,
		useValue:{
			parse:{
				dateInput:['l','LL']
			},
			display:{
				dateInput:'L',
				monthYearLabel:'MMM YYYY',
				dateA11yLabel:'LL',
				monthYearA11yLabel:'MMMM YYYY'
			}
		}
	},
	{
		provide:MAT_DIALOG_DEFAULT_OPTIONS,
		useValue:{
			disableClose:false,
			hasBackdrop:true,
			autoFocus:'first-tabbable',
			closeOnNavigation:true,
			panelClass:'dialog-responsive',
			position:{top:'10px'}
		}
	},
	{
		provide:MAT_SNACK_BAR_DEFAULT_OPTIONS,
		useValue:{
			duration:3000,
			horizontalPosition:'center',
			verticalPosition:'top'
		}
	}
];

export const appConfig:ApplicationConfig={
	providers:[
		provideBrowserGlobalErrorListeners(),
		{provide:ErrorHandler,useClass:GlobalErrorHandler},
		provideZonelessChangeDetection(),
		provideRouter(
			routes,
			withPreloading(SelectivePreloadingStrategyService),
			withRouterConfig({canceledNavigationResolution:'computed'}),
			withInMemoryScrolling({
				scrollPositionRestoration:'enabled'
			})
		),
		provideClientHydration(
			withI18nSupport()
		),
		provideServiceWorker(
			'ngsw-worker.js',
			{
				enabled:!isDevMode(),
				registrationStrategy:'registerWhenStable:30000'
			}
		),
		provideAnimationsAsync(),
		provideHttpClient(
			withInterceptors([httpErrorInterceptor,authenticationInterceptor])
		),
		provideApollo(()=>{
			const httpLink=inject(HttpLink);
			const link:ApolloLink=httpLink.create({uri:`${environment.apiServer.url}/graphql`,withCredentials:true});
			// link=onError((error:ErrorResponse)=>{
			// 	if(error.graphQLErrors) error.graphQLErrors.forEach((value:GraphQLFormattedError,index:number,array:readonly GraphQLFormattedError[]):void=>console.error(`[GraphQL error]:`,error));
			// 	if(error.networkError) console.error('[Network error]:',error.networkError);
			// }).concat(link);
			return {
				link,
				cache:new InMemoryCache(),
				defaultOptions:{
					watchQuery:{
						fetchPolicy:'cache-and-network',
						errorPolicy:'all'
					},
					query:{
						fetchPolicy:'cache-first',
						errorPolicy:'all'
					},
					mutate:{
						fetchPolicy:'no-cache',
						errorPolicy:'none'
					}
				}
			};
		}),
		provideNgxWebstorage(
			withNgxWebstorageConfig({prefix:'app',caseSensitive:true,separator:'-'}),
			withLocalStorage(),
			withSessionStorage()
		),
		provideFirebase(),
		provideConsentTracking(),
		CookieService,
		IconsProvider,
		AuthenticationService,
		AppService,
		provideGalleryOptions({loop:true,autoplay:true,autoplayInterval:10*1000}),
		...appDefaultOptions
	]
};
